import React, { useEffect, useRef, useState } from "react";
import {
  AuthService,
  EmailConfirmationRequiredError,
  UserProfile,
} from "../services/authService";
import { LockIcon, UserPlusIcon } from "./SvgIcons";
import LandingIntro from "./LandingIntro";
import { useLanguage } from "../contexts/LanguageContext";

export interface LoginProps {
  onLogin: (userId: string, userProfile: UserProfile) => void;
  initialStep?: "login" | "signup" | "forgot-password";
  passwordResetComplete?: boolean;
  onForgotPasswordExit?: () => void;
  onContinueToApp?: () => void;
}

const Login: React.FC<LoginProps> = ({
  onLogin,
  initialStep = "login",
  passwordResetComplete = false,
  onForgotPasswordExit,
  onContinueToApp,
}) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<"login" | "signup" | "forgot-password">(
    initialStep,
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetRequested, setResetRequested] = useState(false);
  const authCardRef = useRef<HTMLDivElement>(null);
  const signupNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setStep(initialStep);
    setError("");
    setResetRequested(false);
    setPassword("");

    if (initialStep === "signup") {
      window.requestAnimationFrame(() => {
        authCardRef.current?.scrollIntoView?.({ block: "start" });
        signupNameInputRef.current?.focus();
      });
    }
  }, [initialStep]);

  const handlePasswordResetRequest = async () => {
    if (!email) {
      setError(t("login.errorEmailPassword"));
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      await AuthService.requestPasswordReset(email);
      setResetRequested(true);
    } catch {
      setError(t("login.resetRequestError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordExit = () => {
    setError("");
    setStep("login");
    onForgotPasswordExit?.();
  };

  const handleStartReading = () => {
    setError("");
    setStep("signup");

    window.requestAnimationFrame(() => {
      const prefersReducedMotion =
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
        false;
      authCardRef.current?.scrollIntoView?.({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
      signupNameInputRef.current?.focus();
    });
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError(t("login.errorEmailPassword"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      console.log("🔍 Attempting login for email:", email);

      const userProfile = await AuthService.loginWithEmail(email, password);

      console.log("✅ Login successful:", userProfile);
      if (
        !userProfile.level ||
        !userProfile.interests ||
        userProfile.interests.length === 0
      ) {
        localStorage.setItem(
          "onboardingUserProfile",
          JSON.stringify(userProfile),
        );
        window.location.href = "/onboarding";
        return;
      }
      onLogin(userProfile.id, userProfile);
    } catch (err) {
      console.error("Login error:", err);
      setError(err instanceof Error ? err.message : t("login.errorUnexpected"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!email || !password || !name) {
      setError(t("login.errorAllFields"));
      return;
    }

    if (password.length < 6) {
      setError(t("login.errorPasswordLength"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      console.log("🔍 Starting signup process for:", email);

      const userProfile = await AuthService.signupWithEmail(
        name,
        email,
        password,
        [],
      );

      console.log("✅ Signup successful:", userProfile);

      if (
        !userProfile.level ||
        !userProfile.interests ||
        userProfile.interests.length === 0
      ) {
        localStorage.setItem(
          "onboardingUserProfile",
          JSON.stringify(userProfile),
        );
        window.location.href = "/onboarding";
        return;
      }
      onLogin(userProfile.id, userProfile);
    } catch (err) {
      console.error("Signup error:", err);
      if (err instanceof EmailConfirmationRequiredError) {
        setError(t("login.errorConfirmEmail"));
      } else {
        setError(
          err instanceof Error ? err.message : t("login.errorUnexpected"),
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      console.log("🔍 Starting Google OAuth login...");
      await AuthService.loginWithGoogle();
    } catch (err) {
      console.error("Google login error:", err);
      setError(err instanceof Error ? err.message : t("login.errorGoogle"));
      setIsLoading(false);
    }
  };

  const renderAuthShell = (authCard: React.ReactNode) => (
    <LandingIntro
      authCard={authCard}
      authCardRef={authCardRef}
      onStartReading={handleStartReading}
    />
  );

  const renderLogin = () =>
    renderAuthShell(
      <div className="w-full rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-8 text-center">
          <LockIcon className="mx-auto mb-4 text-3xl text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            {t("login.loginTitle")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t("login.loginSubtitle")}
          </p>
        </div>

        <div className="space-y-4">
          {passwordResetComplete && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <p>{t("login.passwordUpdated")}</p>
              {onContinueToApp && (
                <button
                  type="button"
                  onClick={onContinueToApp}
                  className="mt-3 font-medium text-green-900 underline hover:no-underline"
                >
                  {t("login.continueToApp")}
                </button>
              )}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t("login.email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t("login.emailPlaceholder")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t("login.password")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t("login.passwordPlaceholder")}
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setError("");
              setStep("forgot-password");
            }}
            className="text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            {t("login.forgotPassword")}
          </button>

          <button
            onClick={handleLogin}
            disabled={!email || !password || isLoading}
            className="w-full rounded-lg bg-purple-600 py-3 font-medium text-white transition-colors duration-200 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? t("login.loggingIn") : t("login.loginButton")}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">
                {t("login.orContinue")}
              </span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? t("login.signingIn") : t("login.google")}
          </button>

          <div className="pt-2 text-center text-sm text-gray-500">
            <span>{t("login.noAccount")}</span>
            <button
              onClick={() => setStep("signup")}
              className="font-medium text-purple-600 hover:text-purple-700"
            >
              {t("login.signupLink")}
            </button>
          </div>
        </div>
      </div>,
    );

  const renderSignup = () =>
    renderAuthShell(
      <div className="w-full rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-8 text-center">
          <UserPlusIcon className="mx-auto mb-4 text-3xl text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            {t("login.signupTitle")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t("login.signupSubtitle")}
          </p>
        </div>

        <div className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t("login.name")}
            </label>
            <input
              ref={signupNameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t("login.namePlaceholder")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t("login.email")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t("login.emailPlaceholder")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t("login.password")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t("login.passwordPlaceholder")}
            />
          </div>

          <button
            onClick={handleSignup}
            disabled={!email || !password || !name || isLoading}
            className="w-full rounded-lg bg-purple-600 py-3 font-medium text-white transition-colors duration-200 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? t("login.creatingAccount") : t("login.createAccount")}
          </button>

          <div className="pt-2 text-center text-sm text-gray-500">
            <span>{t("login.hasAccount")}</span>
            <button
              onClick={() => setStep("login")}
              className="font-medium text-purple-600 hover:text-purple-700"
            >
              {t("login.backToLogin")}
            </button>
          </div>
        </div>
      </div>,
    );

  const renderForgotPassword = () =>
    renderAuthShell(
      <div className="w-full rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <h2 className="text-2xl font-bold text-gray-800">
          {t("login.forgotTitle")}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t("login.forgotSubtitle")}
        </p>
        {resetRequested ? (
          <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            {t("login.resetRequested")}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700">
              {t("login.email")}
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3"
              />
            </label>
            <button
              type="button"
              onClick={handlePasswordResetRequest}
              disabled={!email || isLoading}
              className="w-full rounded-lg bg-purple-600 py-3 font-medium text-white disabled:opacity-50"
            >
              {isLoading
                ? t("login.sendingResetLink")
                : t("login.sendResetLink")}
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={handleForgotPasswordExit}
          className="mt-6 font-medium text-purple-600 hover:text-purple-700"
        >
          {t("login.backToLogin")}
        </button>
        {onContinueToApp && (
          <button
            type="button"
            onClick={onContinueToApp}
            className="ml-4 mt-6 font-medium text-gray-700 hover:text-gray-900"
          >
            {t("login.continueToApp")}
          </button>
        )}
      </div>,
    );

  switch (step) {
    case "signup":
      return renderSignup();
    case "forgot-password":
      return renderForgotPassword();
    default:
      return renderLogin();
  }
};

export default Login;
